import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { getSession } from "../backend/auth";
import { isBackendConfigured } from "../backend/client";
import { fetchPendingRequests } from "../backend/friends";
import { save, xpForLevel } from "../systems/Save";
import { sound } from "../systems/Sound";
import { THEME, panel } from "../ui/theme";
import {
  addMenuBackdrop,
  addPageFooter,
  beginSceneLifecycle,
  menuButton,
  type SceneLifecycle,
} from "../ui/menuUi";
import { drawAvatar } from "../ui/avatar";
import { achievementToast } from "../ui/achievementToast";
import { isPortalBuild, portal } from "../portal/portal";
import { warmAtlasPage } from "./MapSelectScene";

const APP_VERSION = "0.2.0";

export class MenuScene extends Phaser.Scene {
  private lifecycle!: SceneLifecycle;
  private settingsLayer?: Phaser.GameObjects.Container;
  private resetLayer?: Phaser.GameObjects.Container;
  private modeLayer?: Phaser.GameObjects.Container;
  private friendsLabel?: Phaser.GameObjects.Text;
  private muteLabel?: Phaser.GameObjects.Text;

  constructor() { super("MenuScene"); }

  create(): void {
    this.lifecycle = beginSceneLifecycle(this);
    achievementToast.attach(this);
    this.settingsLayer = undefined;
    this.resetLayer = undefined;
    this.modeLayer = undefined;
    this.friendsLabel = undefined;
    this.muteLabel = undefined;

    // A menu must never inherit an active world/HUD after an interrupted quit.
    this.scene.stop("UIScene");
    this.scene.stop("GameScene");
    this.scene.stop("SignInScene");
    this.input.enabled = true;

    sound.attach(this);
    sound.setBossLayer(false);
    sound.stopAmbience();
    sound.playMusic("menu", { fadeMs: 900 });

    // First menu = initial download over (portal metric; once per session).
    portal.loadingStop();

    const data = save.load();
    this.drawClassicMenu();

    // Pull the campaign atlas's first page down now, while the player is
    // reading the menu. It is deliberately outside the boot download (see
    // BootScene) but PLAY CAMPAIGN opens straight onto it, so warming it here
    // is what keeps that screen from opening on a loading state.
    warmAtlasPage(this, 0, data);

    this.lifecycle.key("keydown-ESC", () => {
      if (this.resetLayer) this.closeResetConfirm();
      else if (this.settingsLayer) this.closeSettings();
      else if (this.modeLayer) this.closeModeChooser();
    });
    this.lifecycle.add(sound.onChange(() => this.refreshMuteLabel()));
    this.refreshMuteLabel();
    this.refreshFriendsBadge();
  }

  /** Preserve the original open castle composition requested by the user. */
  private drawClassicMenu(): void {
    const cx = GAME_WIDTH / 2;
    if (this.textures.exists("bg-lobby")) {
      const background = this.add.image(cx, GAME_HEIGHT / 2, "bg-lobby").setOrigin(0.5);
      background.setScale(Math.max(GAME_WIDTH / background.width, GAME_HEIGHT / background.height));
      this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.28).setOrigin(0);
    } else {
      this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, THEME.hex.ironDeep).setOrigin(0);
    }

    const title = this.add.text(cx, 56, "MEDIEVAL TOWER DEFENSE", {
      fontFamily: THEME.fonts.display, fontSize: "44px", color: THEME.colors.parchment,
      stroke: THEME.colors.ironDeep, strokeThickness: 4,
    }).setOrigin(0.5);
    this.tweens.add({ targets: title, angle: { from: -0.4, to: 0.4 }, duration: 4200, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });
    this.add.text(cx, 96, "Defend the keep against the horde", {
      fontFamily: THEME.fonts.body, fontSize: "18px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5);

    menuButton(this, 32, 32, 34, 34, "⚙", {
      name: "menu-settings", fontSize: 17, onClick: () => this.scene.launch("SettingsScene"),
    });
    const mute = menuButton(this, 72, 32, 34, 34, sound.isMuted() ? "×" : "♪", {
      name: "menu-mute", fontSize: 18, color: sound.isMuted() ? THEME.colors.blood : THEME.colors.gold,
      onClick: () => { sound.toggleMuted(); this.refreshMuteLabel(); },
    });
    this.muteLabel = mute.getData("label") as Phaser.GameObjects.Text;



    // Portal (guest-only) builds hide the account entry — no sign-in, no
    // profile; progress lives in the local save.
    if (!isPortalBuild()) {
      const accountFrame = panel(this, GAME_WIDTH - 72, 16, 56, 56, "iron");
      accountFrame.setInteractive({ useHandCursor: true });

      // Draw player's active equipped avatar instead of the static woman portrait
      const activeAvatarId = save.load().avatarId || "gules-lion-or";
      drawAvatar(this, GAME_WIDTH - 44, 44, 44, activeAvatarId);

      accountFrame.on("pointerover", () => accountFrame.setTint(0xffeec0));
      accountFrame.on("pointerout", () => accountFrame.clearTint());
      accountFrame.on("pointerdown", () => this.openAccount());
    }

    menuButton(this, cx, 200, 360, 88, "PLAY", {
      name: "menu-play", fontSize: 36, display: true, color: THEME.colors.gold,
      onClick: () => this.startPlay(),
    });
    this.add.text(cx, 262, "Campaign or a quick battle", {
      fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchment,
      stroke: THEME.colors.ironDeep, strokeThickness: 4,
      shadow: { offsetX: 0, offsetY: 2, color: "#000000", blur: 4, fill: true },
    }).setOrigin(0.5);

    const items = [
      { label: this.skillLabel(), scene: "SkillTreeScene", name: "menu-skill-tree" },
      { label: "QUESTS", scene: "QuestScene", name: "menu-quests" },
      { label: "ACHIEVEMENTS", scene: "AchievementsScene", name: "menu-achievements" },
    ];
    // Social features need the Supabase backend — hidden in the guest-only
    // portal build (CrazyGames requires their own account system anyway).
    if (!isPortalBuild()) {
      items.push(
        { label: "FRIENDS", scene: "FriendsScene", name: "menu-friends" },
        { label: "LEADERBOARDS", scene: "LeaderboardScene", name: "menu-leaderboards" },
      );
    }
    if (import.meta.env.DEV) {
      items.push({ label: "MAP EDITOR", scene: "MapEditorScene", name: "menu-map-editor" });
      items.push({ label: "SPRITE SHOWER", scene: "SpriteShowerScene", name: "menu-sprite-shower" });
    }
    items.forEach((item, index) => {
      const action = item.scene === "FriendsScene" ? () => this.openFriends() : () => goTo(this, item.scene);
      const control = menuButton(this, cx, 315 + index * 56, 260, 44, item.label, {
        name: item.name, fontSize: 18, display: true, color: THEME.colors.parchment, onClick: action,
      });
      if (item.scene === "FriendsScene") this.friendsLabel = control.getData("label") as Phaser.GameObjects.Text;
    });

    this.add.text(16, GAME_HEIGHT - 14, `v${APP_VERSION}  •  Medieval Tower Defense`, {
      fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.lock,
    }).setOrigin(0, 1);
  }

  /** First-ever visit: skip the mode choice and drop straight into the campaign
   *  opener with the tutorial (portal requirement: ≤1 click to gameplay).
   *  Returning players pick Kampagne or Schnellspiel. */
  private startPlay(): void {
    const data = save.get();
    const firstVisit = !data.tutorialCompleted && data.stats.runsPlayed === 0;
    if (!firstVisit) {
      this.openModeChooser();
      return;
    }
    this.registry.set("selectedMap", "greenwold_pass");
    this.registry.set("selectedDifficulty", 1);
    this.registry.set("runTutorial", true);
    goTo(this, "GameScene", { launch: ["UIScene"] });
  }

  /** PLAY opens the two ways into a battle: the campaign world map, or the
   *  paged atlas that lets a returning player jump at any unlocked realm. */
  private openModeChooser(): void {
    if (this.modeLayer) return;

    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.68).setOrigin(0).setInteractive();
    dim.on("pointerdown", () => this.closeModeChooser());
    c.add(dim);

    const w = 560, h = 330, x = (GAME_WIDTH - w) / 2, y = (GAME_HEIGHT - h) / 2;
    c.add(panel(this, x, y, w, h, "parchment"));

    c.add(this.add.text(GAME_WIDTH / 2, y + 40, "CHOOSE YOUR BATTLE", {
      fontFamily: THEME.fonts.display, fontSize: "26px", color: THEME.colors.iron,
    }).setOrigin(0.5));

    const modes: { label: string; sub: string; name: string; action: () => void }[] = [
      {
        label: "CAMPAIGN",
        sub: "Travel the realm. Each victory opens the next map.",
        name: "menu-mode-campaign",
        action: () => { this.closeModeChooser(); goTo(this, "CampaignScene"); },
      },
      {
        label: "QUICK BATTLE",
        sub: "Jump straight to any realm you have already opened.",
        name: "menu-mode-quick",
        action: () => { this.closeModeChooser(); this.openMapSelect(); },
      },
    ];
    modes.forEach((mode, index) => {
      const by = y + 106 + index * 104;
      c.add(menuButton(this, GAME_WIDTH / 2, by, w - 96, 56, mode.label, {
        name: mode.name, fontSize: 22, display: true, color: THEME.colors.gold,
        onClick: mode.action,
      }));
      c.add(this.add.text(GAME_WIDTH / 2, by + 44, mode.sub, {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.lock,
        align: "center", wordWrap: { width: w - 110 },
      }).setOrigin(0.5));
    });

    c.add(menuButton(this, GAME_WIDTH / 2, y + h - 34, 160, 40, "BACK", {
      name: "menu-mode-back", fontSize: 15, display: true,
      onClick: () => this.closeModeChooser(),
    }));

    this.modeLayer = c;
  }

  private closeModeChooser(): void {
    this.modeLayer?.destroy();
    this.modeLayer = undefined;
  }

  /** The atlas is an overlay on top of this scene; the menu buttons that stay
   *  visible around it must not keep swallowing clicks underneath. Input is
   *  re-enabled when the atlas closes (and unconditionally in create()). */
  private openMapSelect(): void {
    this.input.enabled = false;
    const atlas = this.scene.get("MapSelectScene");
    atlas.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.enabled = true;
    });
    this.scene.launch("MapSelectScene");
  }

  private drawTopBar(level: number): void {
    panel(this, 22, 14, GAME_WIDTH - 44, 72, "iron-bar").setAlpha(0.98);
    const title = this.add.text(GAME_WIDTH / 2, 37, "MEDIEVAL TOWER DEFENSE", {
      fontFamily: THEME.fonts.display,
      fontSize: "37px",
      color: THEME.colors.gold,
      stroke: THEME.colors.ironDeep,
      strokeThickness: 4,
    }).setOrigin(0.5);
    this.tweens.add({ targets: title, angle: { from: -0.3, to: 0.3 }, duration: 4500, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });
    this.add.text(GAME_WIDTH / 2, 66, "Raise the banners. Hold the realm.", {
      fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5);

    menuButton(this, 58, 50, 56, 44, "⚙", {
      name: "menu-settings", fontSize: 23, onClick: () => this.scene.launch("SettingsScene"),
    });
    const mute = menuButton(this, 124, 50, 56, 44, sound.isMuted() ? "🔇" : "🔊", {
      name: "menu-mute", fontSize: 20, onClick: () => { sound.toggleMuted(); this.refreshMuteLabel(); },
    });
    this.muteLabel = mute.getData("label") as Phaser.GameObjects.Text;

    const signedIn = !isBackendConfigured() || !!getSession();
    menuButton(this, GAME_WIDTH - 124, 50, 210, 44, signedIn ? `COMMANDER  •  LV ${level}` : "SIGN IN / CREATE ACCOUNT", {
      name: "menu-account", fontSize: 14, onClick: () => this.openAccount(),
    });
  }

  private drawCommandTable(): void {
    const x = 84, y = 126, w = 720, h = 530;
    panel(this, x, y, w, h, "iron").setAlpha(0.95);
    panel(this, x + 18, y + 18, w - 36, h - 36, "parchment").setAlpha(0.92);
    this.add.text(x + 38, y + 42, "THE COMMAND TABLE", {
      fontFamily: THEME.fonts.display, fontSize: "25px", color: THEME.colors.iron,
    });
    this.add.text(x + 38, y + 74, "Choose your next order, commander.", {
      fontFamily: THEME.fonts.body, fontSize: "17px", color: THEME.colors.lock,
    });

    menuButton(this, x + w / 2, y + 153, 560, 94, "PLAY CAMPAIGN", {
      name: "menu-play", fontSize: 32, display: true, color: THEME.colors.gold,
      onClick: () => this.scene.launch("MapSelectScene"),
    });
    this.add.text(x + w / 2, y + 215, "Choose a realm, a difficulty, and defend the keep.", {
      fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.lock,
    }).setOrigin(0.5);

    const navY = y + 300;
    const cards: Array<{ label: string; sub: string; action: () => void; name: string; }> = [
      { label: this.skillLabel(), sub: "Spend commander points", action: () => goTo(this, "SkillTreeScene"), name: "menu-skill-tree" },
      { label: "ACHIEVEMENTS", sub: "Inspect your war record", action: () => goTo(this, "AchievementsScene"), name: "menu-achievements" },
      { label: "FRIENDS", sub: "Recruit trusted allies", action: () => this.openFriends(), name: "menu-friends" },
      { label: "LEADERBOARDS", sub: "Compare campaign records", action: () => goTo(this, "LeaderboardScene"), name: "menu-leaderboards" },
    ];
    cards.forEach((card, index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);
      const cx = x + 195 + col * 330;
      const cy = navY + row * 100;
      const container = menuButton(this, cx, cy, 300, 76, card.label, {
        name: card.name, fontSize: 18, display: true, color: THEME.colors.gold, onClick: card.action,
      });
      if (card.label === "FRIENDS") this.friendsLabel = container.getData("label") as Phaser.GameObjects.Text;
      this.add.text(cx, cy + 23, card.sub, {
        fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
      }).setOrigin(0.5);
    });
  }

  private drawCommanderCard(data: ReturnType<typeof save.load>): void {
    const x = 830, y = 126, w = 366, h = 530;
    panel(this, x, y, w, h, "iron").setAlpha(0.96);
    panel(this, x + 18, y + 18, w - 36, h - 36, "parchment").setAlpha(0.94);
    this.add.text(x + w / 2, y + 48, "COMMANDER", {
      fontFamily: THEME.fonts.display, fontSize: "24px", color: THEME.colors.iron,
    }).setOrigin(0.5);

    const portraitX = x + 74, portraitY = y + 113;
    panel(this, portraitX - 48, portraitY - 48, 96, 96, "iron");
    if (this.textures.exists("portrait-idle")) {
      const portrait = this.add.image(portraitX, portraitY, "portrait-idle");
      portrait.setDisplaySize(82, 82);
    } else {
      this.add.text(portraitX, portraitY, "♜", { fontFamily: THEME.fonts.display, fontSize: "44px", color: THEME.colors.gold }).setOrigin(0.5);
    }
    this.add.text(x + 144, y + 96, `LEVEL ${data.playerLevel}`, {
      fontFamily: THEME.fonts.display, fontSize: "25px", color: THEME.colors.iron,
    });
    this.add.text(x + 144, y + 128, `${data.skillPoints} unspent skill ${data.skillPoints === 1 ? "point" : "points"}`, {
      fontFamily: THEME.fonts.body, fontSize: "15px", color: data.skillPoints > 0 ? THEME.colors.forge : THEME.colors.lock,
      wordWrap: { width: 170 },
    });

    const xpY = y + 190, xpW = w - 76;
    this.add.text(x + 38, xpY, "COMMANDER EXPERIENCE", {
      fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.iron,
    });
    this.add.rectangle(x + 38, xpY + 28, xpW, 16, THEME.hex.iron, 0.78).setOrigin(0);
    const need = xpForLevel(data.playerLevel);
    const progress = Math.min(1, data.playerXp / need);
    this.add.rectangle(x + 40, xpY + 30, Math.max(3, (xpW - 4) * progress), 12, THEME.hex.gold, 0.92).setOrigin(0);
    this.add.text(x + w / 2, xpY + 36, `${data.playerXp} / ${need} XP`, {
      fontFamily: THEME.fonts.display, fontSize: "12px", color: THEME.colors.parchment,
    }).setOrigin(0.5);

    const rows = [
      ["BATTLES FOUGHT", String(data.stats.runsPlayed)],
      ["REALMS SAVED", String(data.stats.runsWon)],
      ["WAVES CLEARED", String(data.stats.wavesCleared)],
      ["TROPHIES", String(Object.keys(data.achievements.unlocked).length)],
    ];
    rows.forEach(([label, value], index) => {
      const rowY = y + 270 + index * 48;
      this.add.rectangle(x + 36, rowY, w - 72, 36, THEME.hex.ironDeep, 0.14).setOrigin(0);
      this.add.text(x + 50, rowY + 18, label, { fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.lock }).setOrigin(0, 0.5);
      this.add.text(x + w - 52, rowY + 18, value, { fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.iron }).setOrigin(1, 0.5);
    });
    menuButton(this, x + w / 2, y + h - 54, w - 72, 46, isBackendConfigured() && !getSession() ? "SIGN IN TO SYNC" : "VIEW PROFILE", {
      name: "menu-profile", fontSize: 16, display: true, onClick: () => this.openAccount(),
    });
  }

  private skillLabel(): string {
    const points = save.get().skillPoints;
    return points > 0 ? `SKILL TREE  •  ${points}` : "SKILL TREE";
  }

  private openFriends(): void {
    if (!isBackendConfigured() || getSession()) goTo(this, "FriendsScene");
    else this.openSignIn();
  }

  private openAccount(): void {
    if (!isBackendConfigured() || getSession()) goTo(this, "ProfileScene");
    else this.openSignIn();
  }

  private openSignIn(): void {
    this.scene.pause();
    this.scene.launch("SignInScene");
  }

  private refreshMuteLabel(): void {
    this.muteLabel?.setText(sound.isMuted() ? "×" : "♪");
    this.muteLabel?.setColor(sound.isMuted() ? THEME.colors.blood : THEME.colors.gold);
  }

  private refreshFriendsBadge(): void {
    if (!isBackendConfigured() || !getSession()) return;
    void fetchPendingRequests().then((requests) => {
      if (!this.scene.isActive() || !this.friendsLabel) return;
      const count = requests.filter((request) => request.status === "pending_in").length;
      if (count > 0) {
        this.friendsLabel.setText(`FRIENDS  •  ${count}`);
        this.friendsLabel.setColor(THEME.colors.gold);
      }
    }).catch(() => { /* The menu remains usable when the social service is offline. */ });
  }

  private openSettings(): void {
    if (this.settingsLayer) return;
    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.66).setOrigin(0).setInteractive();
    const w = 580, h = 500, x = (GAME_WIDTH - w) / 2, y = (GAME_HEIGHT - h) / 2;
    const sheet = panel(this, x, y, w, h, "parchment");
    c.add([dim, sheet]);
    c.add(this.add.text(GAME_WIDTH / 2, y + 46, "COMMAND SETTINGS", {
      fontFamily: THEME.fonts.display, fontSize: "28px", color: THEME.colors.iron,
    }).setOrigin(0.5));
    c.add(this.add.text(GAME_WIDTH / 2, y + 78, "Tune the hall before your next battle.", {
      fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.lock,
    }).setOrigin(0.5));

    const makeSlider = (label: string, rowY: number, getValue: () => number, setValue: (value: number) => void) => {
      const left = x + 58, trackX = x + 224, trackW = 250;
      const labelText = this.add.text(left, rowY, label, { fontFamily: THEME.fonts.body, fontSize: "17px", color: THEME.colors.iron }).setOrigin(0, 0.5);
      const track = this.add.rectangle(trackX, rowY, trackW, 12, THEME.hex.iron, 0.72).setOrigin(0, 0.5);
      const fill = this.add.rectangle(trackX + 2, rowY, 2, 8, THEME.hex.gold, 0.95).setOrigin(0, 0.5);
      const knob = this.add.circle(trackX, rowY, 11, THEME.hex.gold).setStrokeStyle(2, THEME.hex.iron);
      const value = this.add.text(x + 506, rowY, "", { fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.iron }).setOrigin(0.5);
      const hit = this.add.zone(trackX + trackW / 2, rowY, trackW + 28, 44).setInteractive({ useHandCursor: true });
      const refresh = () => {
        const amount = Phaser.Math.Clamp(getValue(), 0, 1);
        fill.width = Math.max(2, (trackW - 4) * amount);
        knob.x = trackX + (trackW - 4) * amount + 2;
        value.setText(`${Math.round(amount * 100)}%`);
      };
      const apply = (pointer: Phaser.Input.Pointer) => {
        setValue(Phaser.Math.Clamp((pointer.x - trackX) / trackW, 0, 1));
        refresh();
      };
      hit.on("pointerdown", apply);
      hit.on("pointermove", (pointer: Phaser.Input.Pointer) => { if (pointer.isDown) apply(pointer); });
      c.add([labelText, track, fill, knob, value, hit]);
      refresh();
    };

    const data = save.get();
    makeSlider("Master volume", y + 138, () => data.audio.master, (value) => sound.setMasterVolume(value));
    makeSlider("Music volume", y + 202, () => data.audio.music, (value) => sound.setMusicVolume(value));
    makeSlider("Effects volume", y + 266, () => data.audio.sfx, (value) => sound.setSfxVolume(value));

    const mute = menuButton(this, x + 166, y + 340, 210, 46, sound.isMuted() ? "UNMUTE ALL" : "MUTE ALL", {
      name: "settings-mute", fontSize: 15, display: true, onClick: () => { sound.toggleMuted(); muteLabel(); },
    });
    const muteLabel = () => (mute.getData("label") as Phaser.GameObjects.Text).setText(sound.isMuted() ? "UNMUTE ALL" : "MUTE ALL");
    const lateNight = menuButton(this, x + 414, y + 340, 210, 46, data.audio.lateNight ? "NIGHT MODE: ON" : "NIGHT MODE: OFF", {
      name: "settings-night-mode", fontSize: 14, display: true, onClick: () => {
        sound.setLateNight(!save.get().audio.lateNight);
        (lateNight.getData("label") as Phaser.GameObjects.Text).setText(save.get().audio.lateNight ? "NIGHT MODE: ON" : "NIGHT MODE: OFF");
      },
    });
    const reset = menuButton(this, x + 148, y + 424, 200, 42, "RESET PROGRESS", {
      name: "settings-reset", fontSize: 14, display: true, color: THEME.colors.blood, onClick: () => this.openResetConfirm(),
    });
    const close = menuButton(this, x + 432, y + 424, 200, 42, "CLOSE", {
      name: "settings-close", fontSize: 15, display: true, onClick: () => this.closeSettings(),
    });
    c.add([mute, lateNight, reset, close]);
    dim.on("pointerdown", () => this.closeSettings());
    this.settingsLayer = c;
  }

  private closeSettings(): void {
    this.settingsLayer?.destroy();
    this.settingsLayer = undefined;
    this.refreshMuteLabel();
  }

  private openResetConfirm(): void {
    if (this.resetLayer) return;
    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay + 20);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.72).setOrigin(0).setInteractive();
    const w = 460, h = 220, x = (GAME_WIDTH - w) / 2, y = (GAME_HEIGHT - h) / 2;
    c.add([dim, panel(this, x, y, w, h, "parchment")]);
    c.add(this.add.text(GAME_WIDTH / 2, y + 48, "RESET THE CAMPAIGN?", { fontFamily: THEME.fonts.display, fontSize: "25px", color: THEME.colors.iron }).setOrigin(0.5));
    c.add(this.add.text(GAME_WIDTH / 2, y + 88, "This removes local progress and cannot be undone.", { fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.lock }).setOrigin(0.5));
    const cancel = menuButton(this, x + 126, y + 166, 170, 46, "KEEP PROGRESS", { name: "reset-cancel", fontSize: 13, display: true, onClick: () => this.closeResetConfirm() });
    const confirm = menuButton(this, x + 334, y + 166, 170, 46, "RESET", { name: "reset-confirm", fontSize: 15, display: true, color: THEME.colors.blood, onClick: () => { save.resetAll(); this.closeResetConfirm(); this.closeSettings(); this.scene.restart(); } });
    c.add([cancel, confirm]);
    dim.on("pointerdown", () => this.closeResetConfirm());
    this.resetLayer = c;
  }

  private closeResetConfirm(): void {
    this.resetLayer?.destroy();
    this.resetLayer = undefined;
  }
}
